import { type FC } from 'react'
import { Outlet } from 'react-router'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Box, Button, Icon, IconButton, Typography } from 'shared/ui'
import { ProgressSteps } from 'shared/ui/ProgressSteps'
import { useLastPathSegment } from 'shared/hooks'
import { AISidebar } from 'pages/settings/pages/compliance/ui/AiSidebar/AISidebar'
import { statuses } from 'pages/settings/pages/compliance/ui/AiSidebar/constants'
import type { AISidebarStore } from 'pages/settings/pages/compliance/store/aiSidebarStore'
import styles from './styles.module.scss'

type IStepsCommonLayoutProps = {
  title: string
  progressTitle: string
  totalSteps: number
  settingsStep: number
  closeSetting: () => void
  hideBack?: boolean
  decreaseStep?: (lastPathSegment: string) => void
  sidebarStore?: AISidebarStore
}

export const StepsCommonLayout: FC<IStepsCommonLayoutProps> = observer(
  ({
    title,
    progressTitle,
    settingsStep,
    totalSteps,
    closeSetting,
    decreaseStep,
    sidebarStore,
    hideBack,
  }) => {
    const lastPathSegment = useLastPathSegment()
    const showProgress = totalSteps > 1
    const {
      isVisible,
      isShowHeaderStatus,
      setIsVisible,
      approvalChance = 'medium',
      isMobileView,
      isModalSidebar,
    } = sidebarStore || {}

    const showOpenAiSidebarButton =
      (!isVisible && isShowHeaderStatus) || (isMobileView && isShowHeaderStatus)

    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <div
            className={classNames(
              styles.content__sub,
              showProgress ? styles.content__subWithProgress : styles.content__subWithoutProgress
            )}
          >
            <div className={styles.titleWrap}>
              <Typography
                className={styles.titleWrap__title}
                ariaLabel='stepsPage_title'
                variant='body-xs-regular'
                tag={'div'}
              >
                {title}
              </Typography>
              {showOpenAiSidebarButton && (
                <Box display={'flex'} alignItems={'center'} gap={1.5}>
                  {approvalChance !== 'error' && (
                    <div className={classNames(styles.stepStatusWrap, styles[approvalChance])}>
                      <Icon
                        className={styles.icon}
                        icon={statuses[approvalChance].icon}
                        fontSize={14}
                      />
                      <Typography ariaLabel={'stepStatus_title'} variant={'body-sm-medium'}>
                        {statuses[approvalChance].title}
                      </Typography>
                    </div>
                  )}
                  <Button
                    text='Open AI Checkr'
                    typeBtn='contained'
                    contained='primary'
                    onClick={() => setIsVisible?.(true)}
                    icon={'stars'}
                    size='small'
                    className={styles.openCheckrButton}
                  />
                </Box>
              )}

              {settingsStep > 1 && !hideBack && (
                <div className={styles.backButton}>
                  <IconButton
                    color='tertiary'
                    variant='icon'
                    ariaLabel='back_to_lastStep'
                    icon='arrowLeft'
                    size='medium'
                    onClick={() => decreaseStep?.(lastPathSegment)}
                  />
                </div>
              )}
            </div>

            {showProgress && (
              <div className={styles.localProgress}>
                <ProgressSteps title={progressTitle} total={totalSteps} step={settingsStep} />
              </div>
            )}

            {!showProgress && (
              <Typography
                ariaLabel='ProgressInfo_title'
                variant='heading-md'
                tag='div'
                className={styles.withoutProgressTitle}
              >
                {progressTitle}
              </Typography>
            )}

            <div className={styles.section}>
              <Outlet />
            </div>
          </div>
        </div>
        {!isVisible && (
          <div className={styles.closeButtonWrapper}>
            <IconButton
              className={styles.close}
              ariaLabel='close_setting'
              icon='close'
              variant='icon'
              color='tertiary'
              size='medium'
              onClick={closeSetting}
            />
          </div>
        )}
        {isVisible && !isModalSidebar && <AISidebar sidebarStore={sidebarStore} />}
      </div>
    )
  }
)
